/**
 * What the sync engine sends (back-end-rs/docs/SYNC_ENGINE.md §6). Each
 * model's rows are exactly the objects the REST endpoints return, so the
 * generated client's types describe them.
 */
import {
  AmenityClosureReturn,
  AmenityReturn,
  AmenitySeasonReturn,
  AttachmentReturn,
  CommentReturn,
  CompanyReturn,
  HoaReturn,
  HomeReturn,
  MemberReturn,
  NotificationResponse,
  ReservationReturn,
  UserReturn,
  VendorReturn,
  ViolationReturn,
  WorkOrderReturn,
} from '../clients/rust-api-client';

/** A member's link to a home; the REST API has no DTO for the join row. */
export interface MemberHomeLink {
  id: string;
  member_id: string;
  home_id: string;
}

/**
 * A photo or document on a violation or a comment. REST nests these inside
 * their parent; the copy keeps them as rows of their own, and the hooks
 * put them back where REST had them.
 */
export interface SyncedAttachment extends AttachmentReturn {
  entity_type: 'Violation' | 'Comment';
  entity_id: string;
}

/** One opening window of an amenity's week; REST nests these in the
 *  amenity as `hours`. */
export interface SyncedAmenityHours {
  id: string;
  amenity_id: string;
  weekday: number;
  open_minute: number;
  close_minute: number;
  season_id?: string | null;
}

/** An amenity group's name, which amenity rows show. */
export interface SyncedAmenityGroup {
  id: string;
  hoa_id: string;
  name: string;
}

export interface SyncRows {
  hoa: HoaReturn;
  home: HomeReturn;
  member: MemberReturn;
  memberHome: MemberHomeLink;
  violation: ViolationReturn;
  workOrder: WorkOrderReturn;
  vendor: VendorReturn;
  company: CompanyReturn;
  /** Staff accounts: a company's for its Owner and Admins, otherwise
   *  only the viewer's own (back-end-rs/docs/SYNC_ENGINE.md §5). */
  user: UserReturn;
  /** Violation and work-order threads; an internal comment never reaches
   *  a board member. `attachments` is always empty here. */
  comment: CommentReturn;
  attachment: SyncedAttachment;
  /** Only notifications addressed to staff. What was sent to homeowners
   *  (Communications) is not synced. */
  notification: NotificationResponse;
  /**
   * A booking's own columns. `amenity_name` and `home_address` arrive
   * empty and the hooks fill them from the copy. Never here: the access
   * code and `internal_notes`, and what REST works out per read (the
   * cancel outcome, check-in and access windows, outstanding documents,
   * the refund deadline) — the detail page reads those.
   */
  reservation: ReservationReturn;
  /** An amenity's own columns: no access code or instructions, and
   *  `hours`, `seasons` and `group_name` filled by the hooks. */
  amenity: AmenityReturn;
  amenityHours: SyncedAmenityHours;
  amenitySeason: AmenitySeasonReturn;
  amenityGroup: SyncedAmenityGroup;
  amenityClosure: AmenityClosureReturn;
}

export type ModelName = keyof SyncRows;

export const MODEL_NAMES: ModelName[] = [
  'hoa',
  'home',
  'member',
  'memberHome',
  'violation',
  'workOrder',
  'vendor',
  'company',
  'user',
  'comment',
  'attachment',
  'notification',
  'reservation',
  'amenity',
  'amenityHours',
  'amenitySeason',
  'amenityGroup',
  'amenityClosure',
];

/** Must match `bootstrap::SCHEMA` on the server. */
export const SCHEMA = 4;

export type ModelArrays = { [K in ModelName]: SyncRows[K][] };

export interface BootstrapPayload {
  schema: number;
  lastSyncId: number;
  models: ModelArrays;
}

export interface SyncAction {
  id: number;
  model: ModelName;
  op: 'upsert' | 'delete';
  rowId: string;
  data?: unknown;
}

export type ServerMessage =
  | { type: 'delta'; syncId: number; actions: SyncAction[] }
  | { type: 'live'; lastSyncId: number }
  | { type: 'resync'; reason: string }
  | { type: 'error'; code: string; message?: string }
  | { type: 'pong' };
